import { Casts, Model, Store } from 'store/Base';
import { computed, observable } from 'mobx';
import { Contract } from './Contract';
import { formatMoney } from '../helpers';
import { Moment } from 'moment';
import { BudgetProjectStore } from './BudgetProject';
import { SubprojectStore } from './Subproject';
import { sum } from 'lodash'

// [T54158] Commercial models as used by CY.
export const BUDGET_TYPE_SLA = 'sla';
export const BUDGET_TYPE_ON_DEMAND = 'on demand';
export const BUDGET_TYPE_PROJECT_BASED = 'project based';
export const BUDGET_TYPE_FIXED_CAPACITY_RETAINER = 'fixed capacity retainer';
export const BUDGET_TYPE_FLEXIBLE_CAPACITY_RETAINER = 'flexible capacity retainer';
export const BUDGET_TYPE_CY_INTERNAL = 'cy internal';

export const BUDGET_TYPES = [
    BUDGET_TYPE_SLA,
    BUDGET_TYPE_ON_DEMAND,
    BUDGET_TYPE_PROJECT_BASED,
    BUDGET_TYPE_FIXED_CAPACITY_RETAINER,
    BUDGET_TYPE_FLEXIBLE_CAPACITY_RETAINER,
    BUDGET_TYPE_CY_INTERNAL,
] as const;

// Budget types whose hours count per calendar month.
export const BUDGET_MONTHLY_TYPES = [
    BUDGET_TYPE_SLA,
    BUDGET_TYPE_FIXED_CAPACITY_RETAINER,
    BUDGET_TYPE_FLEXIBLE_CAPACITY_RETAINER,
] as const;

// Budget types where actual hours are billed against an hourly rate.
export const BUDGET_RATE_TYPES = [
    BUDGET_TYPE_ON_DEMAND,
    BUDGET_TYPE_FLEXIBLE_CAPACITY_RETAINER,
] as const;

export const BUDGET_STATUS_PLANNED = 'planned';
export const BUDGET_STATUS_ACTIVE = 'active';
export const BUDGET_STATUS_FINISHED = 'finished';

export const BUDGET_STATUSES = [
    BUDGET_STATUS_PLANNED,
    BUDGET_STATUS_ACTIVE,
    BUDGET_STATUS_FINISHED,
] as const;

export function budgetIsMonthly(type: string): boolean {
    return (BUDGET_MONTHLY_TYPES as readonly string[]).includes(type);
}

export function budgetIsRateBased(type: string): boolean {
    return (BUDGET_RATE_TYPES as readonly string[]).includes(type);
}

export function budgetTypeLabel(type: string): string {
    return {
        [BUDGET_TYPE_SLA]: 'SLA',
        [BUDGET_TYPE_ON_DEMAND]: 'On Demand',
        [BUDGET_TYPE_PROJECT_BASED]: 'Project Based',
        [BUDGET_TYPE_FIXED_CAPACITY_RETAINER]: 'Fixed Capacity Retainer',
        [BUDGET_TYPE_FLEXIBLE_CAPACITY_RETAINER]: 'Flexible Capacity Retainer',
        [BUDGET_TYPE_CY_INTERNAL]: 'CY Internal',
    }[type] || type;
}

export function budgetStatusColor(status: string): string {
    if (status === BUDGET_STATUS_ACTIVE) return 'green';
    if (status === BUDGET_STATUS_FINISHED) return 'grey';
    return 'blue';
}

export class Budget extends Model {
    static backendResourceName = 'budget';
    static omitFields = ['fullName', 'spentHours'];

    @observable id = null;
    @observable name = '';

    // Company - Name (annotation on backend)
    @observable fullName = '';
    @observable type: string = BUDGET_TYPE_PROJECT_BASED;
    @observable contract = this.relation(Contract);

    @observable budgetMoney: null | number = null;
    @observable budgetHours: null | number = null;

    @observable status: string = BUDGET_STATUS_PLANNED;

    @observable hourlyRate: null | number = null;
    @observable minHours: null | number = null;
    @observable maxHours: null | number = null;

    @observable extraHoursArePaid = false;

    @observable startDate: null | Moment = null;
    @observable endDate: null | Moment = null;

    @observable budgetsProjects = this.relation(BudgetProjectStore)

    // [T53675] Sub projects linked to this budget (usage flows back via them).
    @observable subprojects = this.relation(SubprojectStore)

    // Usage annotations from the backend (hours logged via sub project
    // tickets). Only present when requested via with/include_annotations.
    @observable spentHours = 0;
    @observable spentHoursMonth = 0;

    @computed
    get isMonthly(): boolean {
        return budgetIsMonthly(this.type);
    }

    @computed
    get isRateBased(): boolean {
        return budgetIsRateBased(this.type);
    }

    @computed
    get budgetPercentageLeft(): number {
        const percentageSpent = sum(this.budgetsProjects.map(budgetProject => budgetProject.percentage))
        return 100 - percentageSpent;
    }

    // Hours spent against this budget. Monthly budgets are monitored against
    // this month's usage, one-off budgets against the total.
    @computed
    get hoursSpent(): number {
        return this.isMonthly ? this.spentHoursMonth : this.spentHours;
    }

    @computed
    get budgetHoursForComparison(): number {
        if (this.isMonthly) {
            // For flexible retainers the guaranteed minimum is the baseline.
            return this.minHours ?? this.budgetHours ?? 0;
        }
        if (this.budgetHours !== null) {
            return this.budgetHours;
        }
        // [T53675] A money budget with an hourly rate (e.g. a project based
        // budget of 400k€ at 120€/h) is monitored in its hours equivalent.
        if (this.budgetMoney !== null && this.hourlyRate !== null && this.hourlyRate > 0) {
            return this.budgetMoney / this.hourlyRate;
        }
        return 0;
    }

    /**
     * [T53675] Money consumed on this budget: hours spent × hourly rate.
     * Only meaningful for budgets with a rate (project based / on demand /
     * flexible retainer); null otherwise.
     */
    @computed
    get spentMoney(): number | null {
        if (this.hourlyRate === null || this.hourlyRate <= 0) {
            return null;
        }
        return this.hoursSpent * this.hourlyRate;
    }

    @computed
    get hoursOverBudget(): boolean {
        return this.budgetHoursForComparison > 0 && this.hoursSpent > this.budgetHoursForComparison;
    }

    casts() {
        return {
            startDate: Casts.date,
            endDate: Casts.date,
        };
    }

    @computed get description() {
        const money = this.budgetMoney !== null ? formatMoney(this.budgetMoney) : '€ -';
        switch (this.type) {
            case BUDGET_TYPE_SLA:
                return `Monthly ${money} for availability & maintenance. Expected work: ${this.budgetHours ?? 0}h per month.`;
            case BUDGET_TYPE_ON_DEMAND:
                return `Work on demand, actual hours billed at ${this.hourlyRate !== null ? formatMoney(this.hourlyRate) : '€ -'}/h. Indicative budget ${money} / ${this.budgetHours ?? 0}h.`;
            case BUDGET_TYPE_PROJECT_BASED:
                return `Fixed price deliverable: ${money} total, ${this.budgetHours ?? 0}h of work.`;
            case BUDGET_TYPE_FIXED_CAPACITY_RETAINER:
                return `Monthly retainer: ${money} for ${this.budgetHours ?? 0}h of guaranteed capacity per month.`;
            case BUDGET_TYPE_FLEXIBLE_CAPACITY_RETAINER: {
                let d = `Flexible capacity: target ${this.budgetHours ?? 0}h per month`;
                if (this.minHours !== null && this.maxHours !== null) {
                    d += ` (between ${this.minHours}h and ${this.maxHours}h)`;
                }
                if (this.hourlyRate !== null) {
                    d += `, billed at ${formatMoney(this.hourlyRate)}/h`;
                }
                return d + '.';
            }
            case BUDGET_TYPE_CY_INTERNAL:
                return `Internal work to enhance product quality or customer experience. ${this.budgetHours ?? 0}h / ${money}.`;
            default:
                return 'Unrecognized budget type. Please create a ticket';
        }
    }
}

export class BudgetStore extends Store {
    Model = Budget;
    static backendResourceName = 'budget';
}
